// =============================================================================
// features/dashboard/components/CityList.tsx — Lista de cidades com barras
// inline.
//
// SVG manual (sem dep). Lista acessível + barras proporcionais.
// Mostra TODAS as cidades do escopo, dentro de um card de altura fixa com
// rolagem interna. Um indicador flutuante (seta) sinaliza que há mais cidades
// abaixo e desce a lista ao ser clicado — some ao chegar no fim.
// Cores tokens DS.
// =============================================================================

import * as React from 'react';

import type { LeadsByCityItem } from '../../../hooks/dashboard/types';

interface CityListProps {
  data: LeadsByCityItem[];
  /**
   * Rótulo do período. Este card era o exemplo mais citado do defeito D1: a
   * operação selecionava "Hoje" e via "Ariquemes 788" — a contagem histórica —
   * sem nenhum aviso de que aquele card ignorava o filtro. Agora ele respeita o
   * período e diz, no subtítulo, de que período está falando.
   */
  rangeLabel: string;
}

/**
 * Altura útil da lista — equivale a ~5 linhas, que era o tamanho do card quando
 * ele mostrava só o top 5. Mantém o card estável no grid independentemente de
 * quantas cidades a org tiver.
 */
const LIST_MAX_HEIGHT = '13rem';

/** Cores das barras: 1° azul, 2° verde, 3° amarelo, 4° info, demais neutro. */
const BAR_COLORS = [
  'var(--brand-azul)',
  'var(--brand-verde)',
  'var(--brand-amarelo)',
  'var(--info)',
] as const;

function ChevronDownIcon(): React.JSX.Element {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

/**
 * Lista de cidades com barras inline proporcionais, ordenada por volume.
 * Acessível via lista semântica com role="list"; a área rolável é focável por
 * teclado (tabIndex=0) para quem navega sem mouse.
 */
export function CityList({ data, rangeLabel }: CityListProps): React.JSX.Element {
  const sorted = React.useMemo(() => [...data].sort((a, b) => b.count - a.count), [data]);
  const max = sorted[0]?.count ?? 0;
  const total = sorted.reduce((s, d) => s + d.count, 0);
  const isEmpty = sorted.length === 0;

  const listRef = React.useRef<HTMLUListElement>(null);
  const [canScrollDown, setCanScrollDown] = React.useState(false);

  const syncScrollState = React.useCallback(() => {
    const el = listRef.current;
    if (el === null) return;
    // Folga de 2px: zoom/DPI fracionário faz scrollTop+clientHeight nunca bater
    // exatamente em scrollHeight, o que deixaria a seta acesa no fim da lista.
    setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 2);
  }, []);

  // Recalcula quando a lista muda de tamanho (troca de período/cidade no filtro).
  React.useLayoutEffect(() => {
    syncScrollState();
  }, [syncScrollState, sorted.length]);

  // Recalcula quando o card é redimensionado (responsivo, sidebar, zoom).
  React.useEffect(() => {
    const el = listRef.current;
    if (el === null || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(syncScrollState);
    observer.observe(el);
    return () => observer.disconnect();
  }, [syncScrollState]);

  const scrollDown = React.useCallback(() => {
    const el = listRef.current;
    if (el === null) return;
    const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    el.scrollBy({
      top: Math.round(el.clientHeight * 0.8),
      behavior: prefersReduced ? 'auto' : 'smooth',
    });
  }, []);

  return (
    <div
      className="rounded-md border border-border bg-surface-1 p-5"
      style={{ boxShadow: 'var(--elev-2)' }}
    >
      <p
        className="font-sans font-semibold uppercase mb-4"
        style={{ fontSize: '0.7rem', letterSpacing: '0.12em', color: 'var(--text-3)' }}
      >
        Leads por cidade
        {sorted.length > 0 && (
          <span className="ml-1 normal-case font-normal" style={{ letterSpacing: 0 }}>
            ({sorted.length} {sorted.length === 1 ? 'cidade' : 'cidades'})
          </span>
        )}
      </p>
      <p
        className="font-mono -mt-3 mb-4"
        style={{
          fontFamily: 'var(--font-mono, "JetBrains Mono", monospace)',
          fontSize: '0.7rem',
          color: 'var(--text-4)',
        }}
      >
        {total.toLocaleString('pt-BR')} leads — {rangeLabel.toLowerCase()}
      </p>

      {isEmpty || total === 0 ? (
        <div
          className="flex flex-col items-center justify-center py-8 text-center"
          style={{ color: 'var(--text-3)' }}
        >
          <p className="font-sans text-sm">Nenhum lead em {rangeLabel.toLowerCase()}.</p>
          <p className="font-sans text-xs mt-1" style={{ color: 'var(--text-4)' }}>
            Amplie o período para ver as cidades com movimento.
          </p>
        </div>
      ) : (
        <div className="relative">
          <ul
            ref={listRef}
            onScroll={syncScrollState}
            tabIndex={0}
            className="flex flex-col gap-3 overflow-y-auto focus:outline-none focus-visible:ring-2 rounded-xs"
            style={{ maxHeight: LIST_MAX_HEIGHT }}
            role="list"
            aria-label={`Cidades por volume de leads (${sorted.length})`}
          >
            {sorted.map((city, idx) => {
              const pct = max > 0 ? (city.count / max) * 100 : 0;
              const totalPct = total > 0 ? Math.round((city.count / total) * 100) : 0;
              const barColor = BAR_COLORS[idx] ?? 'var(--text-3)';

              return (
                <li key={city.cityId} className="flex flex-col gap-1">
                  <div className="flex items-center justify-between text-xs gap-2">
                    <span
                      className="font-sans font-medium truncate min-w-0"
                      style={{ color: 'var(--text-2)' }}
                    >
                      <span
                        className="font-mono mr-1.5 tabular-nums"
                        style={{
                          fontFamily: 'var(--font-mono, "JetBrains Mono", monospace)',
                          color: barColor,
                          fontSize: '0.65rem',
                          fontWeight: 600,
                        }}
                      >
                        {idx + 1}.
                      </span>
                      {city.cityName}
                    </span>
                    <span
                      className="font-mono shrink-0 tabular-nums"
                      style={{
                        fontFamily: 'var(--font-mono, "JetBrains Mono", monospace)',
                        color: 'var(--text-3)',
                      }}
                    >
                      {city.count.toLocaleString('pt-BR')}
                      <span className="ml-1 opacity-60">({totalPct}%)</span>
                    </span>
                  </div>
                  <div
                    className="h-1.5 rounded-pill overflow-hidden"
                    style={{ background: 'var(--surface-muted)' }}
                    role="presentation"
                    aria-hidden="true"
                  >
                    <div
                      className="h-full rounded-pill transition-all duration-slow"
                      style={{
                        width: `${pct}%`,
                        background: barColor,
                        minWidth: pct > 0 ? '3px' : '0',
                      }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>

          {/* Véu de fade + seta flutuante: só enquanto houver cidade abaixo da dobra.
              O véu é pointer-events-none para não roubar a rolagem da lista. */}
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 transition-opacity duration-fast"
            style={{
              height: '3rem',
              opacity: canScrollDown ? 1 : 0,
              background: 'linear-gradient(to top, var(--bg-elev-1) 15%, transparent)',
            }}
            aria-hidden="true"
          />
          <button
            type="button"
            onClick={scrollDown}
            tabIndex={canScrollDown ? 0 : -1}
            aria-hidden={!canScrollDown}
            className="absolute left-1/2 bottom-1 flex items-center justify-center rounded-pill border transition-all duration-fast hover:-translate-y-0.5 active:scale-95 focus:outline-none focus-visible:ring-2"
            style={{
              width: '1.75rem',
              height: '1.75rem',
              marginLeft: '-0.875rem',
              background: 'var(--bg-elev-1)',
              borderColor: 'var(--border-strong)',
              color: 'var(--text-2)',
              boxShadow: 'var(--elev-2)',
              opacity: canScrollDown ? 1 : 0,
              pointerEvents: canScrollDown ? 'auto' : 'none',
            }}
            title="Ver mais cidades"
          >
            <span className="sr-only">Ver mais cidades</span>
            <ChevronDownIcon />
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

export function CityListSkeleton(): React.JSX.Element {
  return (
    <div
      className="rounded-md border border-border bg-surface-1 p-5"
      style={{ boxShadow: 'var(--elev-2)', minHeight: '200px' }}
    >
      <div
        className="mb-4 h-2.5 w-28 rounded-pill animate-pulse"
        style={{ background: 'var(--surface-muted)' }}
      />
      <div className="flex flex-col gap-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex flex-col gap-1.5">
            <div className="flex justify-between">
              <div
                className="h-2.5 rounded-pill animate-pulse"
                style={{ background: 'var(--surface-muted)', width: `${80 - i * 10}px` }}
              />
              <div
                className="h-2.5 w-12 rounded-pill animate-pulse"
                style={{ background: 'var(--surface-muted)' }}
              />
            </div>
            <div
              className="h-1.5 rounded-pill animate-pulse"
              style={{ background: 'var(--surface-muted)', width: `${70 - i * 12}%` }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
